'use client';
import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import ProgressHero from './components/ProgressHero';
import TrendChartSection from './components/TrendChartSection';
import BeforeAfterTable from './components/BeforeAfterTable';
import GapClosedPanel from './components/GapClosedPanel';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { TrendingUp, ClipboardList } from 'lucide-react';

interface TestSession {
  id: string;
  athleteId: string;
  date: string;
  engineScore: number;
  ignitionScore: number;
  bounceScore: number;
  eps: number;
  trapBarDL: number;
  backSquat: number;
  frontSquat: number;
  splitSquat: number;
  cmjHeight: number;
  sqjHeight: number;
  sprint10yd: number;
  sprint20yd: number;
  medBallThrow: number;
  rsi: number;
  dropJumpHeight: number;
  broadJump: number;
  tripleJump: number;
  pogoHeight: number;
}

export default function ProgressTrendsPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<TestSession[]>([]);
  const [athleteName, setAthleteName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const supabase = createClient();

    async function load() {
      setLoading(true);
      const { data: profile } = await supabase
        .from('athlete_profiles')
        .select('id, name')
        .eq('user_id', user!.id)
        .single();

      if (profile) {
        setAthleteName(profile.name ?? '');
        const { data: sessionData } = await supabase
          .from('test_sessions')
          .select('*')
          .eq('athlete_id', profile.id)
          .order('session_date', { ascending: true });

        if (sessionData) {
          setSessions(sessionData.map((s: any) => ({
            id: s.id,
            athleteId: s.athlete_id,
            date: s.session_date,
            engineScore: Number(s.engine_score),
            ignitionScore: Number(s.ignition_score),
            bounceScore: Number(s.bounce_score),
            eps: Number(s.eps),
            trapBarDL: Number(s.trap_bar_dl ?? 0),
            backSquat: Number(s.back_squat ?? 0),
            frontSquat: Number(s.front_squat ?? 0),
            splitSquat: Number(s.split_squat ?? 0),
            cmjHeight: Number(s.cmj_height ?? 0),
            sqjHeight: Number(s.sqj_height ?? 0),
            sprint10yd: Number(s.sprint_10yd ?? 0),
            sprint20yd: Number(s.sprint_20yd ?? 0),
            medBallThrow: Number(s.med_ball_throw ?? 0),
            rsi: Number(s.rsi ?? 0),
            dropJumpHeight: Number(s.drop_jump_height ?? 0),
            broadJump: Number(s.broad_jump ?? 0),
            tripleJump: Number(s.triple_jump ?? 0),
            pogoHeight: Number(s.pogo_height ?? 0),
          })));
        }
      }
      setLoading(false);
    }

    load();
  }, [user]);

  const first = sessions.length > 0 ? sessions[0] : null;
  const latest = sessions.length > 0 ? sessions[sessions.length - 1] : null;

  const deltas = first && latest ? {
    eps: latest.eps - first.eps,
    engine: latest.engineScore - first.engineScore,
    ignition: latest.ignitionScore - first.ignitionScore,
    bounce: latest.bounceScore - first.bounceScore,
  } : null;

  if (loading) {
    return (
      <AppLayout activePath="/progress-trends">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-3">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-zinc-500">Loading progress data...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout activePath="/progress-trends">
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="page-title">Progress &amp; Trends</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {athleteName && `${athleteName} · `}
              {sessions.length > 0
                ? `${sessions.length} session${sessions.length !== 1 ? 's' : ''} tracked · ${new Date(first!.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })} – ${new Date(latest!.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
                : 'No sessions recorded yet'}
            </p>
          </div>
          <a href="/assessment-entry" className="btn-primary">+ New Assessment</a>
        </div>

        {sessions.length < 2 ? (
          <div
            className="rounded-2xl p-12 text-center"
            style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'rgba(225,6,0,0.1)', border: '1px solid rgba(225,6,0,0.2)' }}
            >
              <TrendingUp size={28} style={{ color: '#E10600' }} />
            </div>
            <h3 className="text-lg font-semibold text-zinc-200 mb-2">
              {sessions.length === 0 ? 'No Assessments Yet' : 'Need One More Assessment'}
            </h3>
            <p className="text-sm text-zinc-500 mb-6 max-w-sm mx-auto">
              {sessions.length === 0
                ? 'Complete your first assessment to start tracking your explosive performance progress over time.'
                : 'You need at least 2 assessments to see progress trends and before/after comparisons.'}
            </p>
            <a href="/assessment-entry" className="btn-primary inline-flex items-center gap-2">
              <ClipboardList size={15} />
              {sessions.length === 0 ? 'Start First Assessment' : 'Add Second Assessment'}
            </a>
          </div>
        ) : (
          <>
            <ProgressHero deltas={deltas!} first={first!} latest={latest!} />
            <TrendChartSection sessions={sessions} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <BeforeAfterTable first={first!} latest={latest!} />
              </div>
              <div>
                <GapClosedPanel first={first!} latest={latest!} />
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}